import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import api, { getErrorMessage } from "@/lib/api";
import Loader from "@/components/Loader";
import { toast } from "sonner";
import { QrCode, Download, ZoomIn, ShieldCheck, Loader2, X, Upload, MessageCircle, CheckCircle2, Clock, XCircle } from "lucide-react";

const UPI_APPS = ["GPay", "PhonePe", "Paytm", "BHIM", "Other"];

function nowLocalInput() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
function fmtDT(s) { try { const d = new Date(s); const p = (n) => String(n).padStart(2, "0"); return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`; } catch { return s; } }

export default function PayQr() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [qrSrc, setQrSrc] = useState("/upi-qr-default.jpeg");
  const [zoom, setZoom] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ utr: "", amount: "", paid_at: nowLocalInput(), upi_app: "GPay" });
  const [file, setFile] = useState(null);
  const [submitted, setSubmitted] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/qr/info");
    setInfo(data);
    setForm((f) => ({ ...f, amount: String(data.amount) }));
    if (data.request && ["pending", "matched", "approved"].includes(data.request.status)) setSubmitted(data.request);
    if (data.qr_fixed || data.qr_custom) {
      try {
        const res = await api.get("/qr/image", { responseType: "blob" });
        setQrSrc(URL.createObjectURL(res.data));
      } catch { /* keep default */ }
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!info) return <Loader />;

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Screenshot must be 3 MB or smaller."); return; }
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) { toast.error("Use a JPG or PNG image."); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const downloadQr = async () => {
    try {
      const res = await fetch(qrSrc);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "pmposhan-upi-qr.jpeg";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download QR."); }
  };

  const submit = async () => {
    if (!/^\d{12}$/.test(form.utr.trim())) { toast.error("UTR must be exactly 12 digits."); return; }
    if (!file) { toast.error("Please upload your payment screenshot."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("utr", form.utr.trim());
      fd.append("amount", form.amount || String(info.amount));
      fd.append("paid_at", form.paid_at);
      fd.append("upi_app", form.upi_app);
      fd.append("screenshot", file);
      const { data } = await api.post("/qr/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSubmitted(data.request);
      toast.success("Submitted for verification!");
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const waMessage = (r) => {
    const date = fmtDT(r.paid_at);
    if (lang === "or") {
      return `🙏 ନମସ୍କାର ଆଡମିନ୍, ମୁଁ PM POSHAN MDM Calculator ସବସ୍କ୍ରିପସନ୍ ପାଇଁ UPI QR ଦ୍ୱାରା ₹${r.amount} ଦେଇଛି।\nନାମ: ${info.name}\nପଞ୍ଜୀକୃତ ମୋବାଇଲ୍: ${info.mobile}\nUTR / Txn ID: ${r.utr}\nପରିମାଣ: ₹${r.amount}\nତାରିଖ: ${date}\nଅନୁରୋଧ ID: ${r.request_code}\nଦୟାକରି ଯାଞ୍ଚ କରି ସକ୍ରିୟ କରନ୍ତୁ। ମୁଁ ସ୍କ୍ରିନସଟ୍ ଯୋଡୁଛି।`;
    }
    return `🙏 Namaskar Admin, I have paid ₹${r.amount} for PM POSHAN MDM Calculator subscription by UPI QR.\nName: ${info.name}\nRegistered mobile: ${info.mobile}\nUTR / Txn ID: ${r.utr}\nAmount: ₹${r.amount}\nDate: ${date}\nRequest ID: ${r.request_code}\nPlease verify and activate. I am attaching the screenshot.`;
  };
  const sendWhatsApp = () => window.open(`https://wa.me/91${info.admin_whatsapp}?text=${encodeURIComponent(waMessage(submitted))}`, "_blank");

  const STEP = ["pending", "matched", "approved"];

  return (
    <div className="max-w-lg mx-auto space-y-5 pb-24 overflow-x-hidden" data-testid="payqr-page">
      <h1 className="text-2xl font-heading font-extrabold flex items-center gap-2"><QrCode className="w-7 h-7 text-thali-green" /> {t("qr_pay_title")}</h1>

      {submitted ? (
        <div className="card-thali space-y-4" data-testid="qr-submitted">
          {submitted.status === "approved" ? (
            <div className="text-center">
              <CheckCircle2 className="w-14 h-14 text-thali-green mx-auto" />
              <div className="font-heading font-extrabold text-xl text-thali-green mt-2">Payment verified & activated! 🎉</div>
              <p className="text-sm text-thali-ink/70 mt-1">Valid until {fmtDT(submitted.validity_to).slice(0, 10)}</p>
              <button onClick={() => nav("/app")} className="btn-3d-green w-full mt-4" data-testid="qr-go-dashboard">Go to Dashboard</button>
            </div>
          ) : (
            <>
              <div className="rounded-xl bg-thali-turmeric/20 p-4 text-center" data-testid="qr-status-pending">
                <Clock className="w-10 h-10 text-thali-saffron mx-auto" />
                <div className="font-heading font-bold text-lg mt-1">⏳ {t("qr_pending_status")}</div>
                <div className="text-sm text-thali-ink/70 mt-1">{t("qr_request_id")}: <b>{submitted.request_code}</b> · UTR: <b>{submitted.utr}</b></div>
              </div>
              <button onClick={sendWhatsApp} className="btn-3d-green w-full text-base" data-testid="qr-send-whatsapp">
                <MessageCircle className="w-5 h-5" /> 📲 {t("qr_send_admin")}
              </button>
              <p className="text-xs text-thali-ink/60 text-center">📎 {t("qr_attach_note")}</p>
            </>
          )}
        </div>
      ) : (
        <>
          {/* Step 1 — Scan & pay */}
          <div className="card-thali text-center space-y-3" data-testid="qr-scan-section">
            <div className="text-lg font-heading font-bold">{t("qr_scan_pay")}</div>
            <button onClick={() => setZoom(true)} className="relative inline-block" data-testid="qr-image-btn">
              <img src={qrSrc} alt="UPI QR" className="w-60 max-w-full mx-auto rounded-xl border border-thali-turmeric/30" data-testid="qr-image" />
              <span className="absolute bottom-2 right-2 bg-black/60 text-white rounded-full p-1"><ZoomIn className="w-4 h-4" /></span>
            </button>
            <div className="text-2xl font-num font-extrabold text-thali-saffron">{t("qr_pay_exact")} ₹{info.amount}</div>
            {info.qr_fixed && <div className="text-xs text-thali-green font-semibold" data-testid="qr-fixed-note">✓ Fixed-amount QR — Razorpay auto-verifies your ₹{info.amount} payment</div>}
            <button onClick={downloadQr} className="btn-ghost w-full justify-center" data-testid="qr-download-btn"><Download className="w-4 h-4" /> ⬇️ {t("qr_download")}</button>
            <div className="grid grid-cols-3 gap-2 text-xs text-thali-ink/70 pt-2">
              {[["1️⃣", t("qr_step1")], ["2️⃣", t("qr_step2")], ["3️⃣", t("qr_step3")]].map(([n, txt], i) => (
                <div key={i} className="rounded-xl bg-thali-cream p-2"><div className="text-lg">{n}</div>{txt}</div>
              ))}
            </div>
            <div className="text-xs text-red-600 bg-red-50 rounded-lg p-2 font-semibold" data-testid="qr-warn">⚠️ {t("qr_warn")}</div>
          </div>

          {/* Step 2 — Proof */}
          <div className="card-thali space-y-3" data-testid="qr-proof-section">
            <div className="font-heading font-bold text-lg">{t("qr_proof_title")}</div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs font-semibold">Name</label><input value={info.name || ""} disabled className="input-thali mt-1 bg-thali-cream/60" /></div>
              <div><label className="text-xs font-semibold">Mobile</label><input value={info.mobile || ""} disabled className="input-thali mt-1 bg-thali-cream/60" /></div>
            </div>
            <div><label className="text-xs font-semibold">{t("qr_utr")}</label>
              <input value={form.utr} onChange={(e) => setForm({ ...form, utr: e.target.value.replace(/\D/g, "").slice(0, 12) })} inputMode="numeric" placeholder="123456789012" className="input-thali mt-1 font-num" data-testid="qr-utr" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs font-semibold">{t("qr_amount")}</label><input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} disabled={info.qr_fixed} className={`input-thali mt-1 ${info.qr_fixed ? "bg-thali-cream/60" : ""}`} data-testid="qr-amount" /></div>
              <div><label className="text-xs font-semibold">{t("qr_app")}</label>
                <select value={form.upi_app} onChange={(e) => setForm({ ...form, upi_app: e.target.value })} className="input-thali mt-1" data-testid="qr-app">
                  {UPI_APPS.map((a) => <option key={a}>{a}</option>)}
                </select></div>
            </div>
            <div><label className="text-xs font-semibold">{t("qr_datetime")}</label><input type="datetime-local" value={form.paid_at} onChange={(e) => setForm({ ...form, paid_at: e.target.value })} className="input-thali mt-1" data-testid="qr-datetime" /></div>
            <div>
              <label className="text-xs font-semibold">{t("qr_screenshot")}</label>
              <label className="mt-1 flex items-center justify-center gap-2 border-2 border-dashed border-thali-turmeric/40 rounded-xl p-4 cursor-pointer text-sm text-thali-ink/60" data-testid="qr-screenshot-label">
                <Upload className="w-4 h-4" /> {file ? file.name : "Tap to choose image"}
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} data-testid="qr-screenshot-input" />
              </label>
              {preview && <img src={preview} alt="preview" className="mt-2 w-28 rounded-lg border border-thali-turmeric/30" data-testid="qr-screenshot-preview" />}
            </div>
            <button onClick={submit} disabled={busy} className="btn-3d-green w-full" data-testid="qr-submit-btn">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />} {t("qr_submit")}
            </button>
          </div>
        </>
      )}

      {zoom && (
        <div className="fixed inset-0 z-[95] bg-black/80 grid place-items-center p-4" onClick={() => setZoom(false)} data-testid="qr-zoom">
          <button className="absolute top-4 right-4 text-white"><X className="w-6 h-6" /></button>
          <img src={qrSrc} alt="UPI QR large" className="max-w-full max-h-full rounded-xl" />
        </div>
      )}
    </div>
  );
}
